import { useState, useEffect, useRef } from 'react';
import './AnimatedCounter.css';

const AnimatedCounter = ({ 
  value = 0, 
  duration = 2000, 
  title = "Count", 
  icon = "👥",
  isUpdating = false 
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const startValueRef = useRef(0);

  useEffect(() => {
    if (value === displayValue) return;

    setIsAnimating(true);
    startValueRef.current = displayValue;
    startTimeRef.current = null;

    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      const currentValue = Math.floor(
        startValueRef.current + (value - startValueRef.current) * easeOutQuart
      );

      setDisplayValue(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
        setIsAnimating(false);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration, displayValue]);

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  // Generate floating user icons
  const generateFloatingIcons = () => {
    const icons = [];
    const iconCount = Math.min(8, Math.floor(value / 100)); // Show more icons as users increase
    
    for (let i = 0; i < iconCount; i++) {
      icons.push(
        <div
          key={i}
          className="floating-icon"
          style={{
            animationDelay: `${i * 0.5}s`,
            left: `${10 + (i * 10)}%`
          }}
        >
          👤
        </div>
      );
    }
    return icons;
  };

  return (
    <div className={`animated-counter ${isUpdating ? 'updating' : ''} ${isAnimating ? 'animating' : ''}`}>
      <div className="counter-background">
        {generateFloatingIcons()}
      </div>
      
      <div className="counter-content">
        <div className="counter-icon">
          <span className={`icon ${isAnimating ? 'pulse' : ''}`}>{icon}</span>
        </div>
        
        <div className="counter-value">
          <span className="number">{formatNumber(displayValue)}</span>
          <div className="counter-title">{title}</div>
        </div>
        
        {isAnimating && (
          <div className="animation-indicator">
            <div className="ripple"></div>
            <div className="ripple"></div>
            <div className="ripple"></div>
          </div>
        )}
      </div>
      
      <div className="counter-glow"></div>
    </div>
  );
};

export default AnimatedCounter;
